import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, Sparkles } from "lucide-react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/ui/page-header";
import { Switch } from "@/components/ui/switch";
import { useScope } from "@/hooks/use-scope";

import {
  HOLD_REASONS,
  listHoldRules, upsertHoldRule, deleteHoldRule,
  previewHoldRule, listRecentHoldDecisions, listTenantCustomers,
  type HoldRuleListRow,
  type HoldReason, type RuleTrigger,
} from "@/lib/hold-rules.functions";
import {
  describeRuleSentence, describeEffect, reasonNounPhrase,
} from "@/lib/hold-rules-language";
import {
  PolicyStatement, ScopeChip, EffectBlock,
  PreviewImpactPanel, RecentDecisions, ProvenanceLine,
} from "@/components/policy-surface";
import { ProposeIntentSheet } from "@/components/intents/propose-intent-sheet";
import { trackPolicyEvent } from "@/lib/policy-telemetry";

export const Route = createFileRoute("/_authenticated/app/admin/hold-rules/advanced")({
  component: HoldRulesAdmin,
  validateSearch: (s) =>
    z.object({ edit: z.string().min(1).optional() }).parse(s),
});

type RuleRow = HoldRuleListRow;

function HoldRulesAdmin() {
  const { tenantId } = useScope();
  useEffect(() => {
    trackPolicyEvent("advanced.opened", {
      tenantId, source: "hold_release",
      props: { editor: "hold_release" },
    });
  }, [tenantId]);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const listFn = useServerFn(listHoldRules);

  const { data, isLoading } = useQuery({
    queryKey: ["hold-rules", tenantId],
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
    enabled: !!tenantId,
  });

  const [editing, setEditing] = useState<RuleRow | "new" | null>(null);
  const [proposeOpen, setProposeOpen] = useState(false);
  const rows = ((data as { rows?: RuleRow[] } | undefined)?.rows ?? []) as RuleRow[];
  const { edit: editParam } = Route.useSearch();
  useEffect(() => {
    if (!editParam || editing) return;
    const found = rows.find((r) => r.id === editParam);
    if (found) {
      setEditing(found);
      navigate({ to: "/app/admin/hold-rules/advanced", search: {}, replace: true });
    }
  }, [editParam, editing, rows, navigate]);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <PageHeader
        title="Hold Release Policies — Advanced editor"
        description="Expert editor for Hold Release policies. The Library is the primary surface; use this only when a policy can't be expressed via Create Policy."
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setProposeOpen(true)} disabled={!tenantId}>
              <Sparkles className="h-4 w-4 mr-1.5" /> Create Policy
            </Button>
            <Button size="sm" onClick={() => setEditing("new")} disabled={!tenantId}>
              <Plus className="h-4 w-4 mr-1.5" /> New rule
            </Button>
          </div>
        }
      />

      {tenantId && (
        <ProposeIntentSheet
          tenantId={tenantId}
          open={proposeOpen}
          onOpenChange={setProposeOpen}
          initialCapability="hold_release"
          onActivated={(capability) => {
            qc.invalidateQueries({ queryKey: ["hold-rules", tenantId] });
            qc.invalidateQueries({ queryKey: ["hold-creation-rules", tenantId] });
            if (capability === "hold_creation") {
              navigate({ to: "/app/admin/hold-creation" });
            }
          }}
        />
      )}

      {editing && tenantId ? (
        <RuleEditor
          tenantId={tenantId}
          initial={editing === "new" ? null : editing}
          existingRules={rows}
          onClose={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["hold-rules", tenantId] });
          }}
        />
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading rules…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No hold-release rules configured yet. Without a rule, holds of any reason must be released manually.
          </p>
          <Button size="sm" className="mt-4" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4 mr-1.5" /> Create first rule
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <RuleListItem
              key={r.id}
              rule={r}
              tenantId={tenantId!}
              onEdit={() => setEditing(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* List item — sentence-style summary                                          */
/* -------------------------------------------------------------------------- */

function RuleListItem({
  rule,
  tenantId,
  onEdit,
}: {
  rule: RuleRow;
  tenantId: string;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const deleteFn = useServerFn(deleteHoldRule);
  const remove = useMutation({
    mutationFn: () => deleteFn({ data: { id: rule.id } }),
    onSuccess: () => {
      toast.success(`${reasonNounPhrase(rule.hold_reason)} rule removed`);
      qc.invalidateQueries({ queryKey: ["hold-rules", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const scopeLabel =
    rule.scope_level === "customer"
      ? `${rule.customers?.name ?? "Customer"} only`
      : "All customers in this tenant";
  return (
    <PolicyStatement status={rule.is_active ? "active" : "inactive"}>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <p>{describeRuleSentence(rule)}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            <ScopeChip label="Reason" value={reasonNounPhrase(rule.hold_reason)} tone="accent" />
            <ScopeChip
              label="Applies to"
              value={scopeLabel}
              tone={rule.scope_level === "customer" ? "accent" : "muted"}
            />
            {rule.source !== "manual" && (
              <ScopeChip label="Source" value={rule.source.replaceAll("_", " ")} tone="muted" />
            )}
          </div>
          <ProvenanceLine updatedAt={rule.updated_at} />
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (confirm(`Remove ${reasonNounPhrase(rule.hold_reason)} rule?`)) remove.mutate();
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </PolicyStatement>
  );
}

/* -------------------------------------------------------------------------- */
/* Editor — sentence-style draft + Preview Impact + Recent Decisions           */
/* -------------------------------------------------------------------------- */

type Draft = {
  holdReason: HoldReason;
  trigger: RuleTrigger;
  afterMinutes: string;
  threshold: string;
  isActive: boolean;
  scopeLevel: "tenant" | "customer";
  customerId: string | null;
};

function RuleEditor({
  tenantId,
  initial,
  existingRules,
  onClose,
}: {
  tenantId: string;
  initial: RuleRow | null;
  existingRules: RuleRow[];
  onClose: () => void;
}) {
  const upsertFn = useServerFn(upsertHoldRule);
  const previewFn = useServerFn(previewHoldRule);
  const recentFn = useServerFn(listRecentHoldDecisions);
  const listCustomersFn = useServerFn(listTenantCustomers);

  const [draft, setDraft] = useState<Draft>(() => ({
    holdReason: initial?.hold_reason ?? HOLD_REASONS[0],
    trigger: initial?.trigger ?? "time_elapsed",
    afterMinutes: String((initial?.params.after_minutes as number | undefined) ?? 60),
    threshold: String((initial?.params.threshold as number | undefined) ?? 0.5),
    isActive: initial?.is_active ?? true,
    scopeLevel: initial?.scope_level ?? "tenant",
    customerId: initial?.customer_id ?? null,
  }));

  const customersQ = useQuery({
    queryKey: ["scope-customers", tenantId],
    queryFn: () => listCustomersFn({ data: { tenantId } }),
    enabled: draft.scopeLevel === "customer",
  });

  const params = useMemo<Record<string, unknown>>(() => {
    if (draft.trigger === "time_elapsed")
      return { after_minutes: Number(draft.afterMinutes) || 0 };
    if (draft.trigger === "fraud_score_below")
      return { threshold: Number(draft.threshold) || 0 };
    return {};
  }, [draft.trigger, draft.afterMinutes, draft.threshold]);

  // Server-side scope shape used by both preview and save — single source.
  const scope = useMemo(() => {
    if (draft.scopeLevel === "tenant") {
      return { scope_level: "tenant" as const, customer_id: null };
    }
    return draft.customerId
      ? { scope_level: "customer" as const, customer_id: draft.customerId }
      : null;
  }, [draft.scopeLevel, draft.customerId]);

  // Reason-uniqueness inside the chosen scope — never block reasons that
  // exist at a different scope, since scopes have independent uniqueness.
  const takenReasons = useMemo(() => {
    const taken = new Set<HoldReason>();
    for (const r of existingRules) {
      if (r.id === initial?.id) continue;
      if (draft.scopeLevel === "tenant" && r.scope_level === "tenant") {
        taken.add(r.hold_reason);
      } else if (
        draft.scopeLevel === "customer" &&
        r.scope_level === "customer" &&
        r.customer_id === draft.customerId
      ) {
        taken.add(r.hold_reason);
      }
    }
    return taken;
  }, [existingRules, initial?.id, draft.scopeLevel, draft.customerId]);

  // Detect the same conflict cases the intent flow surfaces, but for the
  // manual editor. Uses the same `existingRules` list and the same logic.
  const scopeNote = useMemo(() => {
    if (!scope) return null;
    const sameReason = existingRules.filter((r) => r.hold_reason === draft.holdReason);
    const tenantRule = sameReason.find((r) => r.scope_level === "tenant");
    if (scope.scope_level === "customer" && tenantRule) {
      return {
        tone: "info" as const,
        text: `This will specialize the tenant ${draft.holdReason} rule for the selected customer. The tenant rule still applies to other customers.`,
      };
    }
    return null;
  }, [scope, existingRules, draft.holdReason]);

  const sentence = describeRuleSentence({
    hold_reason: draft.holdReason,
    trigger: draft.trigger,
    params,
    is_active: draft.isActive,
  });

  const preview = useQuery({
    queryKey: [
      "hold-rule-preview", tenantId, draft.holdReason, draft.trigger, params,
      scope?.scope_level, scope?.customer_id,
    ],
    queryFn: () =>
      previewFn({
        data: {
          tenantId, holdReason: draft.holdReason, trigger: draft.trigger, params,
          scope: scope!,
        },
      }),
    enabled: !!scope,
  });

  const recent = useQuery({
    queryKey: ["hold-rule-recent", tenantId, draft.holdReason],
    queryFn: () =>
      recentFn({ data: { tenantId, holdReason: draft.holdReason, limit: 8 } }),
  });

  const save = useMutation({
    mutationFn: () => {
      if (!scope) throw new Error("Pick a customer for customer-scoped rules.");
      return upsertFn({
        data: {
          tenantId,
          holdReason: draft.holdReason,
          trigger: draft.trigger,
          params,
          isActive: draft.isActive,
          scope,
        },
      });
    },
    onSuccess: () => {
      toast.success("Rule saved");
      trackPolicyEvent("advanced.saved", {
        source: "hold_release",
        props: { editor: "hold_release" },
      });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reasonOptions = HOLD_REASONS.filter(
    (r) => r === initial?.hold_reason || !takenReasons.has(r),
  );
  const customers = customersQ.data?.rows ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium">
            {initial ? "Edit rule" : "New rule"}
          </h2>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Switch
              checked={draft.isActive}
              onCheckedChange={(v) => setDraft({ ...draft, isActive: v })}
            />
            <span>{draft.isActive ? "Active on save" : "Saved inactive"}</span>
          </div>
        </div>

        <PolicyStatement status={draft.isActive ? "draft" : "inactive"}>
          {sentence}
        </PolicyStatement>

        {/* Scope row — same shape and semantics as the intent flow. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Policy scope</Label>
            <Select
              value={draft.scopeLevel}
              onValueChange={(v) =>
                setDraft({
                  ...draft,
                  scopeLevel: v as "tenant" | "customer",
                  customerId: v === "tenant" ? null : draft.customerId,
                })
              }
              disabled={!!initial}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tenant">Tenant-wide (all customers)</SelectItem>
                <SelectItem value="customer">Specific customer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {draft.scopeLevel === "customer" && (
            <div>
              <Label className="text-xs">Customer</Label>
              <Select
                value={draft.customerId ?? ""}
                onValueChange={(v) => setDraft({ ...draft, customerId: v })}
                disabled={!!initial || customersQ.isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={customersQ.isLoading ? "Loading…" : "Pick a customer"} />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {scopeNote && (
          <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
            {scopeNote.text}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Hold reason</Label>
            <Select
              value={draft.holdReason}
              onValueChange={(v) => setDraft({ ...draft, holdReason: v as HoldReason })}
              disabled={!!initial}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {reasonOptions.map((r) => (
                  <SelectItem key={r} value={r}>{reasonNounPhrase(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              One rule per reason. Held orders matching this reason are governed by this rule.
            </p>
          </div>

          <div>
            <Label className="text-xs">Release behaviour</Label>
            <Select
              value={draft.trigger}
              onValueChange={(v) => setDraft({ ...draft, trigger: v as RuleTrigger })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="time_elapsed">Auto-release after a wait</SelectItem>
                <SelectItem value="credit_recovered">Auto-release when credit recovers</SelectItem>
                <SelectItem value="fraud_score_below">Auto-release when fraud score drops</SelectItem>
                <SelectItem value="manual_only">Manual release only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {draft.trigger === "time_elapsed" && (
            <div>
              <Label className="text-xs">Wait time (minutes)</Label>
              <Input
                type="number"
                min={1}
                value={draft.afterMinutes}
                onChange={(e) => setDraft({ ...draft, afterMinutes: e.target.value })}
              />
            </div>
          )}

          {draft.trigger === "fraud_score_below" && (
            <div>
              <Label className="text-xs">Release when fraud score drops below</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                max={1}
                value={draft.threshold}
                onChange={(e) => setDraft({ ...draft, threshold: e.target.value })}
              />
            </div>
          )}
        </div>

        <EffectBlock label="What happens">
          {describeEffect(draft.trigger, params)}
        </EffectBlock>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !scope}>
            {save.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {initial ? "Save changes" : "Save rule"}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <PreviewImpactPanel
          isLoading={preview.isLoading}
          matchingNow={preview.data?.matchingNow ?? null}
          matchingHistorical={preview.data?.matchingHistorical ?? null}
          examples={preview.data?.examples ?? []}
          emptyHint={`No open ${reasonNounPhrase(draft.holdReason)} holds right now.`}
          footer={
            <p className="text-xs text-muted-foreground">
              Counts include any open hold of this reason in your tenant. Activating
              the rule will not change which orders are on hold — only how they release.
            </p>
          }
        />

        <RecentDecisions
          rows={recent.data?.rows ?? []}
          emptyHint={`No ${reasonNounPhrase(draft.holdReason)} holds in recent history.`}
        />
      </div>
    </div>
  );
}